/**
 * Email Thunks
 * Async actions for email operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../../utils';
import { sendEmailAsSender, generateFormLink, generateConsultationLink, buildEmailContent } from '../api/emailApi';

/**
 * Send request documents email
 */
export const sendRequestDocumentsEmail = createAsyncThunk(
  'email/sendRequestDocuments',
  async ({ to, subject, body, cc, bcc, patientId, onSuccess }, { rejectWithValue }) => {
    try {
      const emailContent = buildEmailContent({ to, subject, body, cc, bcc });
      const response = await sendEmailAsSender(emailContent);

      // Reset open_at so patient gets a fresh 7-day resubmit window
      if (patientId) {
        await axios.patch(
          `${BASE_URL}/documents-approval/${patientId}/submit`,
          { isSubmitted: false },
          { headers: { Authorization: `Bearer ${localStorage.getItem('sessionToken')}` } }
        );
      }

      if (onSuccess) onSuccess();

      return { success: true, message: 'Email sent successfully!', patientId };
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.statusCode === 401
        ? 'Please authorize the Email Sender Account'
        : `Error while sending email: ${error.message}`;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Send patient details form email
 */
export const sendPatientDetailsEmail = createAsyncThunk(
  'email/sendPatientDetails',
  async ({ patientId, patientName, patientEmail, requiredSections }, { rejectWithValue }) => {
    try {
      // Generate form link
      const linkData = await generateFormLink(patientId, patientName, requiredSections);
      const link = linkData?.result?.link;

      if (!patientEmail) {
        return rejectWithValue('There is no email address for this patient.');
      }

      // Create section list
      const enabledSections = Object.entries(requiredSections)
        .filter(([key, enabled]) => enabled)
        .map(([key]) => {
          const sectionNames = {
            personalInformation: 'Personal Information',
            healthQuestions: 'Health Questions',
            symptomsChecklist: 'Symptoms Checklist',
            arbitrationAgreement: 'Arbitration Agreement',
            consentForm: 'Consent Form',
          };
          return sectionNames[key];
        });

      const sectionsList = enabledSections.length > 1
        ? enabledSections.slice(0, -1).join(', ') + ' and ' + enabledSections.slice(-1)
        : enabledSections[0];

      const subject = 'Complete Your Health Information Form Before Your Visit';
      const body = `Dear ${patientName},<br/><br/>` +
        `Thank you for scheduling your appointment at Sunridge Medical. To ensure we provide you with the best possible care, please take a few minutes to complete your Health Information Form before your visit.<br/><br/>` +
        `Your customized form includes the following sections: <strong>${sectionsList}</strong><br/><br/>` +
        `You can access the form here: <a href="${link}" style="color:blue;">${link}</a><br/><br/>` +
        `Completing this in advance will help us streamline your check-in process and focus on your care.<br/><br/>` +
        `Best regards,<br/>Sunridge Medical`;

      const emailContent = buildEmailContent({ to: patientEmail, subject, body });
      await sendEmailAsSender(emailContent);

      return {
        success: true,
        message: `Form request sent successfully! (${enabledSections.length} sections enabled)`,
        patientId
      };
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.statusCode === 401
        ? 'Please authorize the Email Sender Account'
        : `Error while sending email: ${error.message}`;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Send treatment consultation email
 */
export const sendTreatmentConsultationEmail = createAsyncThunk(
  'email/sendTreatmentConsultation',
  async ({ patientId, patientName, patientEmail, requiredSections, additionalClauses }, { rejectWithValue }) => {
    try {
      // Generate consultation link
      const linkData = await generateConsultationLink(patientId, patientName, requiredSections, additionalClauses);
      const link = linkData?.result?.consultFormLink;

      if (!patientEmail) {
        return rejectWithValue('There is no email address for this patient.');
      }

      const subject = 'Treatment Consultation Form - Sunridge Medical';
      const body = `Dear ${patientName},<br/><br/>` +
        `We are scheduling your treatment consultation at Sunridge Medical. Please complete the following consultation form.<br/><br/>` +
        `You can access the form here: <a href="${link}" style="color:blue;">${link}</a><br/><br/>` +
        `Best regards,<br/>Sunridge Medical`;

      const emailContent = buildEmailContent({ to: patientEmail, subject, body });
      await sendEmailAsSender(emailContent);

      return {
        success: true,
        message: 'Treatment consultation request sent successfully!',
        patientId
      };
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.statusCode === 401
        ? 'Please authorize the Email Sender Account'
        : `Error while sending email: ${error.message}`;
      return rejectWithValue(errorMessage);
    }
  }
);
